<script lang="ts" setup>
import { Swiper } from 'swiper';
import { FreeMode, Mousewheel, Navigation, Pagination, Thumbs } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';
import type { OfferImage } from '~/types/Catalog';

defineEmits(['open-fullscreen']);
const { alt, images, isFreeDeliveryAvailable, video } = defineProps<{
  alt: string;
  images: OfferImage[];
  isFreeDeliveryAvailable?: boolean;
  video?: string | null;
}>();

const selectedSlideIndex = defineModel<number>('selectedSlideIndex', { default: 0 });

const catalogStore = useCatalogStore();
const { selectedOffer, selectedSizeId } = storeToRefs(catalogStore);

const mainSwiperInstance = ref<SwiperType | null>(null);
const mainSwiperBlock = ref();

const thumbsSwiperInstance = ref<SwiperType | null>(null);
const thumbsSwiperBlock = ref();

function thumbsSliderInitialization() {
  thumbsSwiperInstance.value = new Swiper(thumbsSwiperBlock.value, {
    direction: 'vertical',
    freeMode: true,
    modules: [Mousewheel, FreeMode, Thumbs],
    mousewheel: true,
    slidesPerView: 'auto',
    watchSlidesProgress: true,
  });
}

function mainSliderInitialization() {
  mainSwiperInstance.value = new Swiper(mainSwiperBlock.value, {
    modules: [Navigation, Pagination, Thumbs],
    navigation: {
      nextEl: '.swiper-button--next',
      prevEl: '.swiper-button--prev',
    },
    pagination: {
      clickable: true,
      el: '.product-page__slider-pagination',
      renderBullet,
    },
    rewind: true,
    slidesPerView: 1,
    spaceBetween: 12,
    thumbs: { swiper: thumbsSwiperInstance.value },
  });

  mainSwiperInstance.value?.on('slideChange', (swiper: SwiperType) => (selectedSlideIndex.value = swiper.realIndex));
}

const renderBullet = (_index: number, className: string) => {
  const isVideoBullet = video && _index === images.length;

  return isVideoBullet
    ? `<div class="${className} product-page__slider-pagination-bullet-wrapper">
          <span class="product-page__slider-pagination-bullet product-page__slider-pagination-bullet--video" />
       </div>`
    : `<div class="${className} product-page__slider-pagination-bullet-wrapper">
         <div class="product-page__slider-pagination-bullet-body">
           <span class="product-page__slider-pagination-bullet" />
         </div>
       </div>`;
};

watch(
  () => selectedSlideIndex.value,
  () => {
    mainSwiperInstance.value?.slideTo(selectedSlideIndex.value);
    thumbsSwiperInstance.value?.slideTo(selectedSlideIndex.value);
  },
);

function handeThumbClick(index: number) {
  selectedSlideIndex.value = index;
  if (mainSwiperInstance.value) mainSwiperInstance.value.realIndex = index;
}

onMounted(async () => {
  thumbsSliderInitialization();
  mainSliderInitialization();
});

onUnmounted(() => {
  mainSwiperInstance.value = null;
  thumbsSwiperInstance.value = null;
  selectedSlideIndex.value = 0;
});
</script>

<template>
  <div
    v-if="images.length"
    class="product-page__gallery"
  >
    <div class="product-page__gallery-swipers">
      <div class="product-page__thumbnails-block">
        <div
          ref="thumbsSwiperBlock"
          class="swiper overflow-hidden !hidden sm:!block max-h-full"
        >
          <div class="swiper-wrapper flex flex-col">
            <div
              v-for="(image, key) in images"
              :key="image.url"
              :class="{ 'product-page__thumbnail--selected': key === selectedSlideIndex }"
              class="swiper-slide product-page__thumbnail"
              @click="handeThumbClick(key)"
            >
              <picture class="flex place-content-center size-full rounded-lg overflow-hidden object-contain">
                <source
                  v-if="image.conversions_webp"
                  :srcset="`${image.conversions_webp.thumb_url_65}, ${image.conversions_webp.thumb_url_180} 2x`"
                  type="image/webp"
                />

                <source
                  v-if="image.conversions"
                  :srcset="image.conversions.thumb_url_65"
                  type="image/jpeg"
                />

                <img
                  :alt
                  :src="image.conversions.thumb_url_65 || image.url"
                  class="object-contain"
                />
              </picture>
            </div>
          </div>
        </div>

        <div
          v-if="video"
          :class="{ 'product-page__thumbnail--selected': selectedSlideIndex === images.length }"
          class="product-page__thumbnail product-page__thumbnail--video"
          @click="mainSwiperInstance?.slideTo(images.length)"
        >
          <SvgoVideoButton
            :font-controlled="false"
            class="size-12 absolute text-violet"
            filled
          />

          <picture class="flex place-content-center size-full rounded-lg overflow-hidden object-contain">
            <source
              v-if="images[0].conversions_webp"
              :srcset="`${images[0].conversions_webp.thumb_url_65}, ${images[0].conversions_webp.thumb_url_180} 2x`"
              type="image/webp"
            />

            <source
              v-if="images[0].conversions"
              :srcset="images[0].conversions.thumb_url_65"
              type="image/jpeg"
            />

            <img
              :alt
              :src="images[0].url"
              class="object-contain"
            />
          </picture>
        </div>
      </div>

      <div
        ref="mainSwiperBlock"
        class="swiper product-page__main-swiper"
      >
        <div class="swiper-wrapper">
          <picture
            v-for="image in images"
            :key="image.url"
            class="swiper-slide flex place-content-center size-full rounded-lg"
            @click="$emit('open-fullscreen')"
          >
            <source
              :srcset="`${image.conversions_webp.thumb_url_360}, ${image.conversions_webp.thumb_url_740} 2x`"
              media="(max-width: 640px)"
              type="image/webp"
            />

            <source
              :srcset="`${image.conversions_webp.thumb_url_740}, ${image.conversions_webp.thumb_url_1480} 2x`"
              type="image/webp"
            />

            <source
              :srcset="image.conversions.thumb_url_740 || image.url"
              type="image/jpeg"
            />

            <img
              :alt
              :src="image.conversions.thumb_url_740 || image.url"
              class="product-page__slider-image"
            />
          </picture>

          <div
            v-if="video"
            class="swiper-slide product-page__slider-video bg-white"
          >
            <iframe
              :src="video"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock"
              allowfullscreen
              height="100%"
              width="100%"
            ></iframe>
          </div>
        </div>

        <BadgesAndFlagsBlock
          v-if="selectedSlideIndex !== images.length"
          :is-free-delivery-available="isFreeDeliveryAvailable"
          :offer="selectedOffer"
          class="product-page__badges-block"
          used-in="offer"
        />

        <div
          v-if="selectedSlideIndex !== images.length"
          class="product-page__right-side-buttons"
        >
          <FavoriteBtn
            :offer="selectedOffer"
            :selected-offer-id="selectedSizeId"
            class="pt-3 px-3 pb-0"
          />

          <ShareBtn class="pt-0 px-3 pb-3" />
        </div>

        <section
          v-if="images.length > 1"
          class="swiper-navigation"
        >
          <div class="swiper-button swiper-button--prev">
            <SvgoChevronLeft
              class="text-2xl"
              filled
            />
          </div>

          <div class="swiper-button swiper-button--next">
            <SvgoChevronRight
              class="text-2xl"
              filled
            />
          </div>
        </section>
      </div>
    </div>

    <section
      :class="{ invisible: images.length === 1 && !video }"
      class="product-page__slider-pagination min-h-6"
    />
  </div>

  <div
    v-else
    class="product-page__no-photo"
    title="Фотографии пока нет"
  >
    <SvgoNoPhotoBig
      :font-controlled="false"
      class="w-24 h-24"
      filled
    />
  </div>
</template>

<style lang="scss" scoped>
.product-page {
  &__gallery {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  &__gallery-swipers {
    position: relative;
    display: flex;
    gap: 8px;
    max-width: calc(100vw - 24px);
    max-height: 640px;

    @include screen-sm {
      gap: 12px;
      max-width: calc(100vw - 48px);
      max-height: 500px;
      background-color: unset;
      aspect-ratio: 12/10;
    }

    @include screen-md {
      aspect-ratio: unset;
    }

    @include screen-lg {
      max-height: 540px;
    }

    @include screen-xl {
      max-height: 640px;
    }

    &:not(:hover) .swiper-navigation {
      opacity: 0;
    }
  }

  &__main-swiper {
    flex-grow: 1;
    height: 100%;
    border-radius: 12px;
    aspect-ratio: 1/1;

    @include screen-sm {
      max-height: 500px;
    }

    @include screen-lg {
      height: auto;
      max-height: 540px;
    }

    @include screen-xl {
      max-height: 640px;
    }
  }

  &__no-photo {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    min-height: 200px;
  }

  &__thumbnails-block {
    display: flex;
    flex-direction: column;
  }

  &__thumbnail {
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    object-fit: cover;
    width: 80px;
    height: 80px;
    margin-bottom: 8px;
    padding: 4px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $white;
    cursor: pointer;
    transition: all 0.3s;

    @apply shadow-sm;

    &:hover {
      border-color: $black-20;
    }

    &--selected {
      border-color: $black-60;
    }

    &--video {
      display: none;

      @include screen-sm {
        display: flex;
        flex-shrink: 0;
        margin-bottom: 0;
      }
    }
  }

  &__slider-image {
    display: flex;
    justify-content: center;
    object-fit: contain;
    width: 100%;
    height: 100%;
    background-color: $white;
    cursor: pointer;
  }

  &__slider-video {
    position: relative;
    container-type: size;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      width: 100%;
      height: calc(100% - 18cqh); // show controls at the bottom
      clip-path: polygon(
        0% 15%,
        0% 100%,
        41% 100%,
        41% 49%,
        59% 49%,
        59% 73%,
        41% 73%,
        41% 100%,
        100% 100%,
        100% 15%
      ); // overlay to show play btn

      @include screen-xs {
        height: calc(100% - 12cqh);
      }

      @include screen-md {
        display: none;
      }
    }
  }

  &__badges-block {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    padding: 12px;
  }

  &__right-side-buttons {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 5;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
